/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Box, FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import {
  Currency,
  FormatMoney,
  formatMoney,
  PathName,
  isNil,
  isNotNil,
} from "~/tools";
import { getChargeTypeLabel } from "../CloudAccountList/utils";
import { Provider } from "../types";

type ChargeTypeCost = {
  chargeType: string;
  cost: string;
  icon?: PathName;
  targetLink?: { pathname: string; search: string };
  ignoreInTotalCost?: boolean;
};

type Props = {
  chargeTypeCosts: ChargeTypeCost[];
  showTitle?: boolean;
  innerSpacing?: keyof typeof theme.spacing;
  provider?: Provider;
};

export function ChargeTypeCostsBar({
  chargeTypeCosts,
  showTitle = true,
  innerSpacing = "spacing04",
  provider,
}: Props) {
  const { currency } = useCurrency();

  const costsWithTotal = [
    {
      chargeType: "Total Costs",
      cost: String(
        chargeTypeCosts.reduce(
          (accumulator, current) =>
            accumulator +
            (current.ignoreInTotalCost ? 0 : Number(current.cost)),
          0
        )
      ),
    },
    ...chargeTypeCosts,
  ];

  return (
    <Box
      innerSpacing={innerSpacing}
      mb={theme.spacing.spacing05}
      css={{ overflowY: "scroll" }}
    >
      <When condition={showTitle}>
        <Text weight="medium">Account Cost Details</Text>
      </When>
      <FlexContainer gap={theme.spacing.spacing03}>
        {costsWithTotal.map(({ chargeType, cost, icon, targetLink }) => (
          <StyledCostItem
            key={chargeType}
            icon={icon}
            label={chargeType}
            cost={cost}
            currency={currency}
            provider={provider}
            targetLink={targetLink}
            itemCount={costsWithTotal.length}
          />
        ))}
      </FlexContainer>
    </Box>
  );
}

function CostItem({
  label,
  cost,
  currency,
  className,
  icon,
  provider,
  targetLink,
}: {
  label: string;
  cost: string;
  currency: Currency;
  className?: string;
  icon?: PathName;
  provider?: Provider;
  targetLink?: { pathname: string; search: string };
}) {
  const formattedCost = formatMoney(cost, currency);

  const costElement = (
    <If condition={!formattedCost.includes("0.00")}>
      <Then>
        <FormatMoney value={cost} currency={currency} />
      </Then>
      <Else>-</Else>
    </If>
  );

  const costElementWithLink = isNil(targetLink) ? (
    <Text weight="medium">{costElement}</Text>
  ) : (
    <Link to={targetLink}>
      <Text weight="medium" color={theme.color.interactive.link}>
        {costElement}
      </Text>
    </Link>
  );

  return (
    <div className={className}>
      <FlexContainer
        columnGap={theme.spacing.spacing01}
        pb={theme.spacing.spacing01}
      >
        {isNotNil(icon) && <SVGIcon name={icon} size="sm" />}
        <Text tag="div" color={theme.color.text.text03} size="sm">
          {getChargeTypeLabel(label, provider)}
        </Text>
      </FlexContainer>
      {costElementWithLink}
    </div>
  );
}

const StyledCostItem = styled(CostItem)<{ itemCount: number }>`
  border-right: solid 1px ${theme.color.border.border01};
  padding-right: ${theme.spacing.spacing03};
  &:last-child {
    border-right: 0;
  }
  width: ${({ itemCount }) =>
    itemCount > 8 ? `calc(100% / ${itemCount})` : "12.5%"};
`;
